import posthog from 'posthog-js';
import {
  AnalyticsEvent,
  type AnalyticsEventName,
  type AssetType,
  type FailureReason,
  type LoginMethod,
} from './events';
import { TRACKING_PLAN_VERSION, validateEvent } from './event-schema';
import { sanitizeProperties, type Properties } from './pii-policy';
import { computePostHogDistinctId } from './distinct-id';
import { isTrackingAllowed, loadConsent, setConsent as persistConsent } from './consent';
import { initGtm, trackGtmSignupConversion } from './gtm';

const POSTHOG_API_KEY = import.meta.env.VITE_POSTHOG_API_KEY ?? '';
const POSTHOG_HOST = import.meta.env.VITE_POSTHOG_HOST ?? 'https://i.wings.money';
const APP_ENV = import.meta.env.VITE_APP_ENV ?? 'DEV';
const IS_DEV = APP_ENV !== 'PROD';

let initialized = false;

class TrackerService {
  private identifiedUserId: string | null = null;
  private identifyInFlight: Promise<void> | null = null;

  init(): void {
    if (initialized) return;
    loadConsent();

    if (POSTHOG_API_KEY) {
      try {
        posthog.init(POSTHOG_API_KEY, {
          api_host: POSTHOG_HOST,
          capture_pageview: false,
          capture_pageleave: true,
          opt_out_capturing_by_default: !isTrackingAllowed(),
          autocapture: false,
          loaded: (ph) => {
            ph.register({
              app_env: APP_ENV,
              platform: 'web',
              tracking_plan_version: TRACKING_PLAN_VERSION,
            });
            ph.setPersonProperties({ app_env: APP_ENV });
          },
        });
      } catch (error) {
        this.devLog('PostHog init failed', error);
      }
    } else {
      this.devLog('VITE_POSTHOG_API_KEY ausente: PostHog desativado (só console).');
    }

    initGtm();
    initialized = true;
  }

  async identify(userId: string, traits?: Properties): Promise<void> {
    if (!isTrackingAllowed()) return;
    this.init();
    const hashedId = await computePostHogDistinctId(userId);
    const safe = sanitizeProperties(traits);
    try {
      posthog.identify(hashedId, { ...safe, app_env: APP_ENV });
      this.identifiedUserId = userId;
    } catch (error) {
      this.devLog('identify failed', error);
    }
  }

  /** Garante identify concluido antes de eventos pos-login (ex.: gate de termos). */
  async ensureIdentified(userId: string, traits?: Properties): Promise<void> {
    if (!userId) return;
    if (this.identifiedUserId === userId) return;
    if (!this.identifyInFlight) {
      this.identifyInFlight = this.identify(userId, traits).finally(() => {
        this.identifyInFlight = null;
      });
    }
    await this.identifyInFlight;
  }

  track(event: AnalyticsEventName, properties?: Properties): void {
    if (!isTrackingAllowed()) return;
    this.init();
    const safe = sanitizeProperties(properties);
    this.validateInDev(event, safe);
    try {
      posthog.capture(event, safe);
    } catch (error) {
      this.devLog('capture failed', error);
    }
    this.devLog(`track ${event}`, safe);
  }

  /** Em web o pageview e automatico via usePageTracking; use isto p/ properties custom. */
  screen(name: string, properties?: Properties): void {
    if (!isTrackingAllowed()) return;
    this.init();
    const safe = sanitizeProperties(properties);
    try {
      posthog.capture('$pageview', { $screen_name: name, ...safe });
    } catch (error) {
      this.devLog('screen failed', error);
    }
  }

  trackCta(ctaId: string, screen?: string, extra?: Properties): void {
    this.track(AnalyticsEvent.CtaTap, { cta_id: ctaId, screen, ...extra });
  }

  trackAssetCreate(
    stage: 'started' | 'completed' | 'abandoned' | 'failed',
    assetType: AssetType,
    extra?: Properties,
  ): void {
    const event =
      stage === 'started'
        ? AnalyticsEvent.AssetCreateStarted
        : stage === 'completed'
          ? AnalyticsEvent.AssetCreateCompleted
          : stage === 'failed'
            ? AnalyticsEvent.AssetCreateFailed
            : AnalyticsEvent.AssetCreateAbandoned;
    this.track(event, { asset_type: assetType, ...extra });
  }

  trackLogin(
    stage: 'attempt' | 'success' | 'failure',
    method: LoginMethod,
    extra?: Properties,
  ): void {
    const event =
      stage === 'attempt'
        ? AnalyticsEvent.AuthLoginAttempt
        : stage === 'success'
          ? AnalyticsEvent.AuthLoginSuccess
          : AnalyticsEvent.AuthLoginFailure;
    this.track(event, { method, ...extra });
  }

  trackSignUp(
    stage: 'started' | 'submitted' | 'failed' | 'completed',
    extra?: Properties,
  ): void {
    const event =
      stage === 'started'
        ? AnalyticsEvent.AuthSignUpStarted
        : stage === 'submitted'
          ? AnalyticsEvent.AuthSignUpSubmitted
          : stage === 'failed'
            ? AnalyticsEvent.AuthSignUpFailed
            : AnalyticsEvent.AuthSignUpCompleted;
    this.track(event, extra);
    if (stage === 'completed') {
      trackGtmSignupConversion(extra);
    }
  }

  trackError(
    screen: string,
    reasonCode?: FailureReason | string,
    extra?: Properties,
  ): void {
    this.track(AnalyticsEvent.ErrorShown, {
      screen,
      code: reasonCode,
      ...extra,
    });
  }

  trackRequestFailed(
    endpoint: string,
    statusCode: number,
    method: string,
  ): void {
    this.track(AnalyticsEvent.ErrorRequestFailed, {
      endpoint,
      status_code: statusCode,
      method,
    });
  }

  captureException(error: unknown, context?: Properties): void {
    const err = error instanceof Error ? error : new Error(String(error));
    const safe = sanitizeProperties(context);
    try {
      posthog.capture('$exception', {
        $exception_message: err.message,
        $exception_type: err.name,
        ...safe,
      });
    } catch {
      // ignore
    }
    this.devLog('exception', { message: err.message, ...safe });
  }

  async setConsent(value: 'granted' | 'denied'): Promise<void> {
    persistConsent(value);
    this.init();
    try {
      if (value === 'denied') {
        try {
          posthog.capture(AnalyticsEvent.ConsentAnalyticsChanged, {
            granted: false,
            app_env: APP_ENV,
          });
        } catch {
          // ignore
        }
        posthog.opt_out_capturing();
      } else {
        posthog.opt_in_capturing();
        initGtm();
        this.track(AnalyticsEvent.ConsentAnalyticsChanged, { granted: true });
      }
    } catch {
      // ignore
    }
  }

  reset(): void {
    this.identifiedUserId = null;
    this.identifyInFlight = null;
    try {
      posthog.reset();
    } catch {
      // ignore
    }
  }

  private validateInDev(event: AnalyticsEventName, properties: Properties): void {
    if (!IS_DEV) return;
    for (const w of validateEvent(event, properties)) {
      console.warn(`[analytics][schema] ${w}`);
    }
  }

  private devLog(label: string, payload?: unknown): void {
    if (IS_DEV) console.log(`[analytics] ${label}`, payload ?? '');
  }
}

export const Tracker = new TrackerService();
export { AnalyticsEvent, computePostHogDistinctId };
export type { AnalyticsEventName, AssetType, LoginMethod, FailureReason };
